/**
 * ShortcutLegend.tsx — the discoverable "?" help for the cockpit's keyboard
 * shortcuts (wired in AppShell). A fixed bottom-right toggle opens a small
 * non-modal popover listing the panel-switch (g h / g l / g c) and refresh (r)
 * shortcuts, so a returning power-user can drive the cockpit without the mouse
 * and a newcomer can find out how.
 *
 * Accessibility: the toggle is a real <button> with aria-expanded/aria-controls;
 * the popover is a labelled region; every shortcut key is rendered as a <kbd>.
 * Escape closes it (handled globally in AppShell).
 */

import { useTranslation } from 'react-i18next';

export interface ShortcutLegendProps {
  open: boolean;
  onToggle: () => void;
  panelId: string;
}

const Kbd = ({ children }: { children: string }) => (
  <kbd className="rounded bg-surface-3 px-1.5 py-0.5 font-mono text-meta text-ink-1 ring-1 ring-inset ring-ink-3/30">
    {children}
  </kbd>
);

export const ShortcutLegend = ({ open, onToggle, panelId }: ShortcutLegendProps) => {
  const { t } = useTranslation();
  return (
    <div className="fixed bottom-4 right-4 z-20 flex flex-col items-end gap-2">
      {open ? (
        <section
          id={panelId}
          aria-label={t('shortcuts.legendTitle')}
          data-testid="shortcuts-legend"
          className="flex w-72 flex-col gap-2 rounded-xl bg-surface-2 p-4 text-body ring-1 ring-inset ring-ink-3/20"
        >
          <h2 className="text-meta font-semibold uppercase tracking-wide text-ink-3">
            {t('shortcuts.legendTitle')}
          </h2>
          <p className="text-meta text-ink-3">{t('shortcuts.prefixHint')}</p>
          <ul className="flex flex-col gap-1.5 text-ink-1">
            <li className="flex items-center justify-between gap-3">
              <span>{t('shortcuts.gotoHealth')}</span>
              <span className="flex gap-1">
                <Kbd>g</Kbd>
                <Kbd>h</Kbd>
              </span>
            </li>
            <li className="flex items-center justify-between gap-3">
              <span>{t('shortcuts.gotoLeads')}</span>
              <span className="flex gap-1">
                <Kbd>g</Kbd>
                <Kbd>l</Kbd>
              </span>
            </li>
            <li className="flex items-center justify-between gap-3">
              <span>{t('shortcuts.gotoConversations')}</span>
              <span className="flex gap-1">
                <Kbd>g</Kbd>
                <Kbd>c</Kbd>
              </span>
            </li>
            <li className="flex items-center justify-between gap-3">
              <span>{t('shortcuts.refresh')}</span>
              <Kbd>r</Kbd>
            </li>
            <li className="flex items-center justify-between gap-3">
              <span>{t('shortcuts.toggle')}</span>
              <Kbd>?</Kbd>
            </li>
          </ul>
        </section>
      ) : null}

      <button
        type="button"
        onClick={onToggle}
        data-testid="shortcuts-toggle"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('shortcuts.open')}
        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-surface-2 font-mono text-body text-ink-1 ring-1 ring-inset ring-ink-3/30 hover:bg-surface-3 focus:outline-none focus:ring-2 focus:ring-outline-focus"
      >
        <span aria-hidden="true">?</span>
      </button>
    </div>
  );
};
